import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import noop from "lodash/noop";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useReducer } from "react";
/**
 * Modal from zephyr has 'name' property, but our primitive modal doesn't,
 * so we check displayName if it exists to filter out that modal
 */ const isSameModal = (a, b)=>{
    if ("name" in a && "name" in b) {
        return a.name === b.name;
    } else {
        return a.displayName === b.displayName;
    }
};
const reducer = (state, action)=>{
    switch(action.type){
        case "show":
            return [
                ...state.filter((param)=>{
                    let { Modal } = param;
                    return !isSameModal(Modal, action.Modal);
                }),
                {
                    Modal: action.Modal,
                    props: action.props
                }
            ];
        case "hide":
            {
                return state.filter((param)=>{
                    let { Modal } = param;
                    return !isSameModal(Modal, action.Modal);
                });
            }
        case "hide-all":
            return [];
        default:
            return state;
    }
};
const defaultValue = noop;
const ModalProviderContext = /*#__PURE__*/ createContext(defaultValue);
const ModalContainer = (param)=>{
    let { props, Modal, onClose } = param;
    const closeModal = useCallback(()=>{
        onClose(Modal);
    }, [
        Modal,
        onClose
    ]);
    useEffect(()=>{
        window.addEventListener("popstate", closeModal);
        return ()=>window.removeEventListener("popstate", closeModal);
    }, [
        Modal,
        closeModal,
        onClose
    ]);
    return /*#__PURE__*/ _jsx(Modal, {
        onClose: closeModal,
        ...props
    });
};
export const ModalProvider = /*#__PURE__*/ memo((param)=>{
    let { children } = param;
    const [state, dispatch] = useReducer(reducer, []);
    const onClose = useCallback((Modal)=>dispatch({
            type: "hide",
            Modal
        }), []);
    return /*#__PURE__*/ _jsxs(ModalProviderContext.Provider, {
        value: dispatch,
        children: [
            children,
            state.map((param, index)=>{
                let { Modal, props } = param;
                return /*#__PURE__*/ _jsx(ModalContainer, {
                    Modal: Modal,
                    onClose: onClose,
                    props: props
                }, Modal.name || Modal.displayName || index);
            })
        ]
    });
});
ModalProvider.displayName = "ModalProvider";
/**
 * This hook allows you to show/hide and modal component you want by just passing the component. It will return you a show and hide function respectively.
 * @param Modal The modal component you want to show
 */ export function useAirModal(Modal) {
    const dispatch = useContext(ModalProviderContext);
    if (dispatch === defaultValue) {
        throw "ModalProviderContext used outside of ModalProvider";
    }
    const values = useMemo(()=>{
        // @ts-ignore
        const showFn = (props)=>dispatch({
                type: "show",
                Modal,
                props
            });
        const hideFn = ()=>dispatch({
                type: "hide",
                Modal
            });
        return [
            showFn,
            hideFn
        ];
    }, [
        dispatch,
        Modal
    ]);
    return values;
}
export const useModalContext = ()=>{
    const dispatch = useContext(ModalProviderContext);
    if (dispatch === defaultValue) {
        throw "ModalProviderContext used outside of ModalProvider";
    }
    return {
        dispatch
    };
};
